import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Input,
    Button,
    Textarea,
    Checkbox,
    Typography,
    Alert
} from '@material-tailwind/react';
import axios from 'axios';
import { useAuth } from '../myTrack/AuthContext';
import { rootUrl } from '../../config/config';

export default function ChargeTypeDialog({
    open,
    type = null,
    onSave,
    onClose
}) {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        default_rate: '',
        is_active: true
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (type) {
            setFormData({
                code: type.code || '',
                name: type.name || '',
                description: type.description || '',
                default_rate: type.default_rate || '',
                is_active: type.is_active !== false
            });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                default_rate: '',
                is_active: true
            });
        }
        setErrors({});
        setError(null);
    }, [type, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.code?.trim()) {
            newErrors.code = 'Code is required';
        }
        if (!formData.name?.trim()) {
            newErrors.name = 'Name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            setError(null);

            const url = type
                ? `${rootUrl}/api/job-card-charge-types/${type.id}`
                : `${rootUrl}/api/job-card-charge-types`;

            const method = type ? 'put' : 'post';

            const payload = {
                code: formData.code?.toUpperCase(),
                name: formData.name,
                description: formData.description,
                default_rate: formData.default_rate ? parseFloat(formData.default_rate) : null
            };

            if (type) {
                payload.is_active = formData.is_active;
            }

            const response = await axios[method](url, payload, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });

            if (response.data.success) {
                onSave();
                onClose();
            } else {
                setError(response.data.message || 'Failed to save charge type');
            }
        } catch (err) {
            console.error('Error saving charge type:', err);
            setError(err.response?.data?.message || 'Failed to save charge type');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} handler={onClose} size="sm">
            <DialogHeader>
                {type ? 'Edit Charge Type' : 'Add New Charge Type'}
            </DialogHeader>

            <DialogBody divider>
                {error && <Alert color="red" className="mb-4">{error}</Alert>}

                <div className="space-y-4">
                    {/* Code */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Code * {type && '(Read-only)'}
                        </label>
                        <Input
                            value={formData.code}
                            onChange={(e) => !type && handleChange('code', e.target.value?.toUpperCase())}
                            disabled={!!type}
                            placeholder="e.g., LABOR, TRANSPORT"
                            error={!!errors.code}
                        />
                        {errors.code && (
                            <Typography className="text-xs text-red-500 mt-1">{errors.code}</Typography>
                        )}
                    </div>

                    {/* Name */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Name *
                        </label>
                        <Input
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value?.toUpperCase())}
                            placeholder="e.g., Labor Charges"
                            error={!!errors.name}
                        />
                        {errors.name && (
                            <Typography className="text-xs text-red-500 mt-1">{errors.name}</Typography>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Description
                        </label>
                        <Textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Enter description"
                            rows={2}
                        />
                    </div>

                    {/* Default Rate */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Default Rate (Optional)
                        </label>
                        <Input
                            type="number"
                            value={formData.default_rate}
                            onChange={(e) => handleChange('default_rate', e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="text-right"
                        />
                    </div>

                    {/* Active Toggle */}
                    {type && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={formData.is_active}
                                onChange={(e) => handleChange('is_active', e.target.checked)}
                                label="Active"
                            />
                        </div>
                    )}
                </div>
            </DialogBody>

            <DialogFooter>
                <Button
                    variant="text"
                    color="gray"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    color="blue"
                    onClick={handleSave}
                    loading={loading}
                    disabled={loading}
                >
                    {type ? 'Update' : 'Add'} Charge Type
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
